import { useCallback, useEffect, useState } from 'react';
import { Loader2, Eye, Send, RotateCcw, Save } from 'lucide-react';
import { contentSnapshot } from '@/content';
import { Field } from '@/components/ContentFields';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTIONS = [
  ['BRAND', 'Brand & Logo'],
  ['CONTACT', 'Contact & Links'],
  ['HERO', 'Intro / Hero'],
  ['MARQUEE_ITEMS', 'Ticker Line'],
  ['READY_NOW', 'Ch 01 — Proposition'],
  ['EXPLORE', 'Ch 02 — Building & Hotspots'],
  ['HOMES', 'Ch 03 — Homes'],
  ['PLANS', 'Ch 04 — Floor Plans'],
  ['CONNECTIVITY', 'Ch 05 — Connectivity'],
  ['SPECS', 'Ch 06 — Specifications'],
  ['JOURNEY', 'Journey Scenes'],
  ['ENQUIRY', 'Enquiry Form Text'],
  ['FOOTER', 'Footer & Compliance'],
];

function cloneSet(obj, path, value) {
  if (path.length === 1) {
    const next = Array.isArray(obj) ? [...obj] : { ...obj };
    next[path[0]] = value;
    return next;
  }
  const next = Array.isArray(obj) ? [...obj] : { ...obj };
  next[path[0]] = cloneSet(obj[path[0]], path.slice(1), value);
  return next;
}

export default function ContentEditor() {
  const [data, setData] = useState(null); // {draft, published, dirty}
  const [section, setSection] = useState('READY_NOW');
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    const res = await fetch(`${API}/admin/content`, { credentials: 'include' });
    const d = await res.json();
    if (d.draft == null) {
      // first run: initialise the draft from the site's current bundled content
      const snap = contentSnapshot();
      await fetch(`${API}/admin/content`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ draft: snap }),
      });
      const res2 = await fetch(`${API}/admin/content`, { credentials: 'include' });
      setData(await res2.json());
      setMsg('Content panel initialised from the current website.');
      return;
    }
    setData(d);
  }, []);

  useEffect(() => { load().catch(() => setErr('Could not load content.')); }, [load]);

  const onChange = (path, value) => {
    setData((d) => ({ ...d, draft: cloneSet(d.draft, path, value) }));
    setMsg('');
  };

  const call = async (label, fn, okMsg) => {
    setBusy(label);
    setErr('');
    setMsg('');
    try {
      await fn();
      await load();
      setMsg(okMsg);
    } catch (e) {
      setErr(e.message || 'Action failed');
    } finally {
      setBusy('');
    }
  };

  const saveDraft = () => call('save', async () => {
    const res = await fetch(`${API}/admin/content`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
      body: JSON.stringify({ draft: data.draft }),
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Save failed');
  }, 'Draft saved. Preview it, then Publish when ready.');

  const publish = () => call('publish', async () => {
    const res = await fetch(`${API}/admin/content/publish`, { method: 'POST', credentials: 'include' });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Publish failed');
  }, 'Published — your changes are now live for all visitors.');

  const discard = () => call('discard', async () => {
    await fetch(`${API}/admin/content/discard`, { method: 'POST', credentials: 'include' });
  }, 'Draft reset to the currently published content.');

  if (!data) return <div className="py-16 text-center text-sm text-[#646A72]"><Loader2 className="mx-auto animate-spin" /></div>;

  const sectionValue = data.draft ? data.draft[section] : null;

  return (
    <div data-testid="content-editor">
      {/* action bar */}
      <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-[#E4E6EB] bg-white p-3">
        <button data-testid="content-save-draft-btn" onClick={saveDraft} disabled={!!busy} className="btn-ghost !px-5 !py-2.5 !text-[11px]">
          {busy === 'save' ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save Draft
        </button>
        <button data-testid="content-publish-btn" onClick={publish} disabled={!!busy || !data.dirty} className="btn-primary !px-5 !py-2.5 !text-[11px] disabled:opacity-40">
          {busy === 'publish' ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Publish
        </button>
        <button data-testid="content-discard-btn" onClick={discard} disabled={!!busy || !data.dirty} className="btn-ghost !px-5 !py-2.5 !text-[11px] disabled:opacity-40">
          <RotateCcw size={15} /> Discard Draft
        </button>
        <a data-testid="content-preview-btn" href="/?preview=draft" target="_blank" rel="noreferrer"
          className="flex h-10 items-center gap-2 rounded-full border border-[#E4E6EB] bg-white px-5 font-display text-[11px] font-bold uppercase tracking-[0.14em] text-[#404145] transition-colors hover:border-[#C92F4B] hover:text-[#C92F4B]">
          <Eye size={15} /> Preview Draft
        </a>
        <span data-testid="content-status" className={`ml-auto rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.12em] ${data.dirty ? 'bg-[#C92F4B]/10 text-[#A41225]' : 'bg-[#1C7C4D]/10 text-[#1C7C4D]'}`}>
          {data.dirty ? 'Unpublished changes' : 'Live & in sync'}
        </span>
      </div>
      {msg && <p data-testid="content-message" className="mt-3 rounded-xl bg-[#1C7C4D]/8 px-4 py-3 text-xs font-semibold text-[#1C7C4D]">{msg}</p>}
      {err && <p data-testid="content-error" className="mt-3 rounded-xl bg-[#A41225]/8 px-4 py-3 text-xs font-semibold text-[#A41225]">{err}</p>}

      <div className="mt-5 grid gap-5 lg:grid-cols-[220px_1fr]">
        {/* section picker */}
        <nav className="flex gap-2 overflow-x-auto lg:flex-col lg:overflow-visible" data-testid="content-sections">
          {SECTIONS.map(([key, label]) => (
            <button key={key} data-testid={`content-section-${key.toLowerCase()}`} onClick={() => setSection(key)}
              className={`whitespace-nowrap rounded-full px-4 py-2.5 text-left font-display text-[11px] font-bold uppercase tracking-[0.12em] transition-colors lg:rounded-xl ${
                section === key ? 'bg-[#1C1D21] text-white' : 'border border-[#E4E6EB] bg-white text-[#404145] hover:border-[#C92F4B] hover:text-[#C92F4B]'
              }`}>
              {label}
            </button>
          ))}
        </nav>

        {/* fields */}
        <div className="space-y-3 rounded-2xl border border-[#E4E6EB] bg-white p-4 sm:p-5" data-testid="content-fields">
          {sectionValue == null && <p className="text-sm text-[#646A72]">This section has no editable content.</p>}
          {Array.isArray(sectionValue)
            ? <Field name={section.toLowerCase()} value={sectionValue} path={[section]} onChange={onChange} />
            : sectionValue && Object.entries(sectionValue).map(([k, v]) => (
                <Field key={k} name={k} value={v} path={[section, k]} onChange={onChange} />
              ))}
          <p className="pt-2 text-[11px] text-[#A7ABB0]">Changes apply after <strong>Save Draft</strong> → <strong>Publish</strong>. Use Preview Draft to check the site first.</p>
        </div>
      </div>
    </div>
  );
}
